/**
 * OverrideCaptureModal (DC-1518, v3.0 Block 3).
 *
 * The HITL correction: corrected classification + required reason, framed as
 * a permanent audit record — gravity without friction. cv_judgment_overrides
 * is append-only (reason NOT NULL, RESTRICT), so the copy states the audit
 * fact plainly in body text; nothing is going wrong, so no warning banner.
 *
 * Submits to POST /api/cv-judgments/:id/override (DC-1517). The reason
 * product bar (≥ 10 chars) is enforced by the API too — the modal mirrors it
 * so the user learns the bar before the round-trip.
 *
 * UX spec: docs/design/v3.0-domain-pack-surfaces-ux-spec.md §3.
 */

import React, { useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { JudgmentOverride } from './packJudgmentTypes';

export const REASON_MIN_LENGTH = 10;
const OTHER_VALUE = '__other__';

export interface ClassificationOption {
  value: string;
  label: string;
  group: 'subdomain' | 'archetype';
}

export interface OverrideCaptureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  judgmentId: string;
  /** The effective classification the reviewer is correcting against. */
  currentClassification: string;
  confidence: number | null;
  packRef: string;
  /** Pack subdomain ids + archetype ids; the vocabulary the select seeds from. */
  options: ClassificationOption[];
  /** Fired with the recorded override on success (after the modal closes). */
  onRecorded: (override: JudgmentOverride) => void;
}

export function OverrideCaptureModal({
  open,
  onOpenChange,
  judgmentId,
  currentClassification,
  confidence,
  packRef,
  options,
  onRecorded,
}: OverrideCaptureModalProps): React.ReactElement {
  const [selected, setSelected] = useState('');
  const [freeText, setFreeText] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subdomainOptions = useMemo(() => options.filter((o) => o.group === 'subdomain'), [options]);
  const archetypeOptions = useMemo(() => options.filter((o) => o.group === 'archetype'), [options]);

  const newClassification = (selected === OTHER_VALUE ? freeText : selected).trim();
  const reasonTrimmed = reason.trim();
  const reasonValid = reasonTrimmed.length >= REASON_MIN_LENGTH;
  const canSubmit = newClassification.length > 0 && reasonValid && !submitting;

  const disabledTitle = !newClassification
    ? 'Choose or enter a corrected classification first'
    : !reasonValid
      ? `The reason must be at least ${REASON_MIN_LENGTH} characters — this becomes a permanent audit record`
      : undefined;

  const resetForm = () => {
    setSelected('');
    setFreeText('');
    setReason('');
    setError(null);
  };

  /** Esc / outside-click: make abandoning easy, but never silently eat a typed reason. */
  const confirmDiscard = (event: { preventDefault: () => void }) => {
    if (reasonTrimmed.length > 0 && !window.confirm('Discard your reason?')) {
      event.preventDefault();
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetForm();
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(`/api/cv-judgments/${judgmentId}/override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_classification: newClassification, reason: reasonTrimmed }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          data?.error?.message || data?.error || `Override failed (${response.status})`
        );
      }
      const override = data.override as JudgmentOverride;
      resetForm();
      onOpenChange(false);
      onRecorded(override);
    } catch (err) {
      // Failure: inline error above the buttons, modal stays open, input preserved.
      setError(err instanceof Error ? err.message : 'Failed to record override');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dp-modal-overlay" />
        <Dialog.Content
          className="dp-modal"
          aria-describedby="dp-override-permanence"
          onEscapeKeyDown={confirmDiscard}
          onInteractOutside={confirmDiscard}
          onOpenAutoFocus={(event) => {
            // Focus lands on the classification select (spec §3.2).
            const select = (event.currentTarget as HTMLElement | null)?.querySelector('select');
            if (select instanceof HTMLElement) {
              event.preventDefault();
              select.focus();
            }
          }}
        >
          <p className="dp-modal-eyebrow">Override — permanent audit record</p>
          <Dialog.Title className="dp-modal-title">Override this judgment</Dialog.Title>
          <Dialog.Close asChild>
            <button type="button" className="dp-modal-close" aria-label="Close">
              <X size={16} aria-hidden="true" />
            </button>
          </Dialog.Close>

          <div className="dp-field">
            <span className="dp-field-label">Current classification</span>
            <span className="dp-current-classification">
              {currentClassification}
              {confidence !== null && ` · confidence ${confidence}`}
              {` · ${packRef}`}
            </span>
          </div>

          <div className="dp-field">
            <label className="dp-field-label" htmlFor="dp-override-classification">
              Corrected classification *
            </label>
            <select
              id="dp-override-classification"
              className="dp-select"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="" disabled>
                Select a classification…
              </option>
              {subdomainOptions.length > 0 && (
                <optgroup label="Subdomains">
                  {subdomainOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </optgroup>
              )}
              {archetypeOptions.length > 0 && (
                <optgroup label="Archetypes">
                  {archetypeOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </optgroup>
              )}
              <option value={OTHER_VALUE}>Other…</option>
            </select>
            {selected === OTHER_VALUE && (
              <input
                type="text"
                className="dp-input"
                aria-label="Corrected classification (free text)"
                placeholder="Enter the corrected classification"
                value={freeText}
                maxLength={255}
                onChange={(e) => setFreeText(e.target.value)}
              />
            )}
          </div>

          <div className="dp-field">
            <label className="dp-field-label" htmlFor="dp-override-reason">
              Reason *
            </label>
            <textarea
              id="dp-override-reason"
              className="dp-textarea"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <div className="dp-helper">
              <span>At least {REASON_MIN_LENGTH} characters.</span>
              <span className="dp-helper-counter" aria-hidden="true">
                {reasonTrimmed.length}/{REASON_MIN_LENGTH}
              </span>
            </div>
          </div>

          <p className="dp-permanence" id="dp-override-permanence">
            Recorded permanently with your name and timestamp. The original judgment is
            preserved; your correction supersedes it and feeds the next pack version.
          </p>

          {error && (
            <p className="dp-modal-error" role="alert">
              {error}
            </p>
          )}

          <div className="dp-modal-actions">
            <Dialog.Close asChild>
              <button type="button" className="dp-btn-cancel" disabled={submitting}>
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              className="dp-btn-primary"
              disabled={!canSubmit}
              title={disabledTitle}
              onClick={handleSubmit}
            >
              {submitting ? (
                <>
                  <span className="spinner-small" aria-hidden="true"></span> Recording…
                </>
              ) : (
                'Record override'
              )}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default OverrideCaptureModal;
